export const RESULT_VIEW_STATE = {
  UNAVAILABLE: 'unavailable',
  NOT_FORMED: 'notFormed',
  HIT: 'hit',
  MISS: 'miss',
  NOT_PARTICIPATED: 'notParticipated',
};

export const RESULT_FETCH_MESSAGES = {
  failed: {
    title: '結果を表示できませんでした',
    description:
      '通信状況またはデータ状態により、ラウンド結果を取得できませんでした。画面を更新しても解消しない場合は、しばらく時間をおいて再度お試しください。',
  },
  idle: {
    title: '次のラウンドから参加できます',
    description: '次のラウンドが始まるまでお待ちください。',
  },
};

export const ROUND_NOT_FORMED_DISPLAY_MESSAGE = {
  title: 'このラウンドは不成立となりました',
  participatedDescription: 'ベットが片方の選択肢に集中したため、成立に必要なベットが集まりませんでした。ベットしたルームチップは返還されています。',
  notParticipatedDescription: 'ベットが片方の選択肢に集中したため、このラウンドは不成立となりました。',
  noBetsDescription: 'このラウンドにはベットが入らなかったため、不成立となりました。',
  disputeUnresolvedDescription: '結果について判断が一致しなかったため、このラウンドは不成立になりました。ベットは全額返却されます。',
};

const getRoundNotFormedDescription = ({ participated, reason }) => {
  if (reason === 'no_bets') {
    return ROUND_NOT_FORMED_DISPLAY_MESSAGE.noBetsDescription;
  }

  if (reason === 'result_dispute_unresolved') {
    return ROUND_NOT_FORMED_DISPLAY_MESSAGE.disputeUnresolvedDescription;
  }

  return participated
    ? ROUND_NOT_FORMED_DISPLAY_MESSAGE.participatedDescription
    : ROUND_NOT_FORMED_DISPLAY_MESSAGE.notParticipatedDescription;
};

export const formatSignedPoint = (value) => {
  const numericValue = Number(value || 0);
  const sign = numericValue > 0 ? '+' : '';
  return `${sign}${numericValue.toLocaleString()}`;
};

const formatRate = (rate) => {
  const numericRate = Number(rate);
  return Number.isFinite(numericRate) ? numericRate.toFixed(2) : '-';
};

export const buildMemberResultViewModel = ({ user, dialogDatas, resultFetchState }) => {
  const result = dialogDatas?.result;

  if (!result) {
    const fetchState = resultFetchState || 'unknown';
    return {
      state: RESULT_VIEW_STATE.UNAVAILABLE,
      fetchState,
      message: RESULT_FETCH_MESSAGES[fetchState] || null,
    };
  }

  if (result.round_not_formed) {
    const participated = Boolean(result.current_user_participated);
    const description = getRoundNotFormedDescription({
      participated,
      reason: result.round_not_formed_reason,
    });

    return {
      state: RESULT_VIEW_STATE.NOT_FORMED,
      participated,
      message: {
        title: ROUND_NOT_FORMED_DISPLAY_MESSAGE.title,
        description,
      },
    };
  }

  const answers = Array.isArray(result.answers) ? result.answers : [];
  const currentUserId = user?.id;
  const rankingRows = Array.isArray(result.event_users) ? result.event_users : [];
  const currentUserRankingIndex = rankingRows.findIndex((row) => String(row?.user_id) === String(currentUserId));
  const currentUserRankingRow = currentUserRankingIndex >= 0 ? rankingRows[currentUserRankingIndex] : null;
  const currentUserDisplayRank = (() => {
    const rank = Number(currentUserRankingRow?.rank);
    return Number.isFinite(rank) && rank > 0 && rank !== 999 ? rank : null;
  })();
  const currentUserRank = (() => {
    if (currentUserDisplayRank) {
      return currentUserDisplayRank;
    }

    return currentUserRankingIndex >= 0 ? currentUserRankingIndex + 1 : null;
  })();
  const currentUserBeforeRank = (() => {
    const beforeRank = Number(currentUserRankingRow?.before_rank);
    return Number.isFinite(beforeRank) && beforeRank > 0 && beforeRank !== 999 ? beforeRank : null;
  })();
  const currentUserWinStreak = (() => {
    const winStreak = Number(currentUserRankingRow?.win_streak);
    return Number.isFinite(winStreak) && winStreak >= 2 ? winStreak : null;
  })();
  const currentUserParticipated = Boolean(result.current_user_participated);
  const resultSelection = (() => {
    const selection = Number(result.result);
    if (!Number.isInteger(selection) || selection < 0) {
      return null;
    }

    return {
      selection,
      label: answers[selection] || `選択肢 ${selection + 1}`,
    };
  })();
  const userResults = (result.users || []).filter((row) => Number(row.user_id) === Number(currentUserId));
  const totalBet = userResults.reduce((sum, row) => sum + Number(row.bet || 0), 0);
  const totalRefund = userResults.reduce((sum, row) => sum + Number(row.refund_point || 0), 0);
  const displayTotalBet = totalBet > 0 ? totalBet : Number(result.current_user_bet_total || 0);
  const displayTotalRefund = totalRefund;
  const correctResults = userResults.filter((row) => Number(row.refund_point || 0) > 0);
  const state = correctResults.length > 0
    ? RESULT_VIEW_STATE.HIT
    : currentUserParticipated
      ? RESULT_VIEW_STATE.MISS
      : RESULT_VIEW_STATE.NOT_PARTICIPATED;

  return {
    state,
    answers,
    resultSelection,
    correctResults: correctResults.map((row) => ({
      ...row,
      label: answers[row.selection] || `選択肢 ${Number(row.selection) + 1}`,
      rateLabel: formatRate(row.rate),
    })),
    selections: userResults.map((row) => ({
      ...row,
      label: answers[row.selection] || `選択肢 ${Number(row.selection) + 1}`,
      rateLabel: formatRate(row.rate),
    })),
    summary: {
      totalBet: displayTotalBet,
      totalRefund: displayTotalRefund,
      balance: displayTotalRefund - displayTotalBet,
    },
    all: {
      participantsCount: result.all?.participantsCount,
      topResult: result.all?.topResult?.[0] ?? null,
    },
    ranking: {
      currentUserId,
      currentUserBeforeRank,
      currentUserDisplayRank,
      currentUserRank,
      currentUserWinStreak,
      rows: rankingRows,
      totalCount: rankingRows.length,
    },
  };
};
